import React, { useCallback, useEffect, useState } from 'react'
import SubcontractorSiteDetailDrawer from './SubcontractorSiteDetailDrawer'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory, useRouteMatch } from 'react-router-dom'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Refresh } from '@material-ui/icons'
import {
  PortalLoadingCard,
  PortalStateCard,
  formatPortalDate
} from './portalShared'

// ── Hero accent configs — flat surface + thin colored left border ──
const HERO_CARDS = [
  { key: 'completedToday', label: 'Completed today', border: 'var(--df-primary)' },
  { key: 'overdue', label: 'Overdue', border: 'var(--df-accent)' },
  { key: 'photosToday', label: 'Photos today', border: 'var(--df-info-text)' },
  { key: 'openIssues', label: 'Open issues', border: 'var(--df-danger-text)' },
  { key: 'crewActive', label: 'Crew active', border: 'var(--df-ink-3)' }
]

// ── Styles ─────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  heroRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2.5)
  },
  heroCard: {
    flex: '1 1 160px',
    minWidth: 140,
    borderRadius: 'var(--df-radius-md)',
    boxShadow: 'var(--df-shadow)',
    position: 'relative',
    overflow: 'hidden'
  },
  heroCardContent: {
    padding: theme.spacing(2, 2.5),
    '&:last-child': { paddingBottom: theme.spacing(2) }
  },
  heroValue: {
    fontSize: 26,
    fontWeight: 600,
    lineHeight: 1.1,
    color: 'var(--df-ink)',
    fontFamily: 'var(--df-font-mono)'
  },
  heroLabel: {
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginTop: theme.spacing(0.5),
    color: 'var(--df-ink-3)',
    fontFamily: 'var(--df-font-mono)'
  },
  refreshBtn: {
    alignSelf: 'center',
    flexShrink: 0
  },

  // Quadrant Card shell
  quadrantCard: {
    borderRadius: 'var(--df-radius-md)',
    boxShadow: 'var(--df-shadow)',
    border: '1px solid var(--df-line)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  quadrantHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: 'var(--df-surface)',
    borderBottom: '1px solid var(--df-line)',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5)
  },
  quadrantTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: 'var(--df-ink)'
  },
  quadrantSubtitle: {
    fontSize: '0.75rem',
    color: 'var(--df-ink-3)',
    marginLeft: theme.spacing(1)
  },
  quadrantBody: {
    flex: 1,
    maxHeight: 340,
    overflowY: 'auto',
    padding: theme.spacing(1, 0)
  },
  quadrantFooter: {
    borderTop: '1px solid var(--df-line)',
    padding: theme.spacing(1, 2),
    textAlign: 'right'
  },
  footerLink: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'var(--df-accent)',
    fontFamily: 'var(--df-font-mono)',
    letterSpacing: '0.03em'
  },

  // Activity feed
  feedItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid var(--df-line)',
    '&:last-child': { borderBottom: 'none' }
  },
  feedItemUrgent: {
    backgroundColor: 'var(--df-accent-soft)'
  },
  feedIcon: {
    width: 24,
    height: 24,
    fontSize: '0.7rem',
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 2
  },
  feedBody: {
    flex: 1,
    minWidth: 0
  },
  feedPrimary: {
    fontSize: '0.83rem',
    color: 'var(--df-ink)',
    lineHeight: 1.35
  },
  feedSecondary: {
    fontSize: '0.75rem',
    color: 'var(--df-ink-3)',
    marginTop: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  feedTime: {
    fontSize: '0.7rem',
    color: 'var(--df-ink-4)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginTop: 2
  },
  thumbnailRow: {
    display: 'flex',
    gap: 4,
    marginTop: 4,
    flexWrap: 'wrap'
  },
  thumbnail: {
    width: 40,
    height: 40,
    borderRadius: 4,
    objectFit: 'cover',
    border: '1px solid var(--df-line)'
  },

  // Agenda
  agendaSectionLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: theme.spacing(1, 2, 0.5, 2),
    fontFamily: 'var(--df-font-mono)'
  },
  agendaItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 2),
    borderBottom: '1px solid var(--df-line)',
    '&:last-child': { borderBottom: 'none' }
  },
  agendaSiteName: {
    fontWeight: 600,
    fontSize: '0.83rem',
    color: 'var(--df-ink)'
  },
  agendaAddress: {
    fontSize: '0.73rem',
    color: 'var(--df-ink-3)'
  },

  // Manage
  crewRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--df-line)',
    '&:last-child': { borderBottom: 'none' }
  },
  crewName: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: 'var(--df-ink)'
  },
  crewMeta: {
    fontSize: '0.73rem',
    color: 'var(--df-ink-3)'
  },
  coverageRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: theme.spacing(2),
    borderBottom: '1px solid var(--df-line)'
  },
  coverageStat: {
    textAlign: 'center'
  },
  coverageValue: {
    fontSize: '1.4rem',
    fontWeight: 800,
    color: 'var(--df-ink)',
    fontFamily: 'var(--df-font-mono)'
  },
  coverageLabel: {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: 'var(--df-ink-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontFamily: 'var(--df-font-mono)'
  },
  quickLinks: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    flexWrap: 'wrap'
  },
  pillBtn: {
    borderRadius: 20,
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '4px 14px',
    border: '1px solid var(--df-line-2)',
    color: 'var(--df-ink)',
    '&:hover': { backgroundColor: 'var(--df-surface-2)' }
  },

  // Office
  messageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--df-line)',
    '&:last-child': { borderBottom: 'none' }
  },
  msgIconRead: {
    width: 28,
    height: 28,
    backgroundColor: 'var(--df-muted-bg)',
    fontSize: '0.7rem',
    flexShrink: 0
  },
  msgIconUnread: {
    width: 28,
    height: 28,
    backgroundColor: 'var(--df-accent)',
    fontSize: '0.7rem',
    flexShrink: 0
  },
  msgBody: {
    flex: 1,
    minWidth: 0
  },
  msgTitle: {
    fontSize: '0.83rem',
    color: 'var(--df-ink)',
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  msgTitleUnread: {
    fontWeight: 700
  },
  msgPreview: {
    fontSize: '0.75rem',
    color: 'var(--df-ink-3)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  msgMeta: {
    fontSize: '0.68rem',
    color: 'var(--df-ink-4)',
    marginTop: 2
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'var(--df-accent)',
    flexShrink: 0,
    marginTop: 6
  },

  // Badge chips
  headerChip: {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    fontFamily: 'var(--df-font-mono)',
    letterSpacing: '0.03em'
  }
}))

// ── Helpers ────────────────────────────────────────────────────────

function formatShortTime (timestamp) {
  if (!timestamp) return ''
  const d = new Date(timestamp)
  if (Number.isNaN(d.getTime())) return ''
  let h = d.getHours()
  const m = d.getMinutes().toString().padStart(2, '0')
  const suffix = h >= 12 ? 'p' : 'a'
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${m}${suffix}`
}

function relativeTime (timestamp) {
  if (!timestamp) return ''
  const d = new Date(timestamp)
  if (Number.isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function feedIconProps (type) {
  switch (type) {
    case 'completion':
      return { children: '\u2713', style: { backgroundColor: 'var(--df-ok-bg)', color: 'var(--df-ok-text)' } }
    case 'photo':
      return { children: '\uD83D\uDCF7', style: { backgroundColor: 'var(--df-info-bg)', color: 'var(--df-info-text)' } }
    case 'issue':
      return { children: '\u26A0', style: { backgroundColor: 'var(--df-accent-soft)', color: 'var(--df-accent)' } }
    case 'note':
      return { children: '\uD83D\uDCDD', style: { backgroundColor: 'var(--df-muted-bg)', color: 'var(--df-ink-3)' } }
    default:
      return { children: '\u2022', style: { backgroundColor: 'var(--df-muted-bg)', color: 'var(--df-muted-text)' } }
  }
}

function feedActionText (item) {
  switch (item.type) {
    case 'completion':
      return ' completed'
    case 'photo':
      return ` added ${item.photoCount || ''} photo${(item.photoCount || 0) !== 1 ? 's' : ''}`
    case 'issue':
      return ` reported ${item.issueCategory || 'an issue'}`
    case 'note':
      return ' left a note'
    default:
      return ''
  }
}

function feedPropertyLabel (item) {
  return item.siteName || item.relatedJobLabel || ''
}

// ── Component ──────────────────────────────────────────────────────

const SubcontractorDashboardV2 = ({ user }) => {
  const classes = useStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [data, setData] = useState(null)
  const [selectedScheduleItemId, setSelectedScheduleItemId] = useState(null)

  const profile = user?.subcontractorProfile || {}
  const isAdmin = profile.role === 'admin'

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get('/api/subcontractor/dashboard-v2')
      setData(response.data || null)
    } catch (err) {
      console.error('Failed to load dashboard v2', err)
      setError(err?.response?.data?.message || 'Unable to load dashboard data right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  // ── Loading / Error / Empty ──
  if (loading) {
    return <PortalLoadingCard title="Loading command center" message="Pulling the latest field data..." minHeight={300} />
  }

  if (error) {
    return (
      <PortalStateCard
        title="Dashboard unavailable"
        message={error}
        actionLabel="Retry"
        onAction={loadDashboard}
        tone="danger"
        minHeight={280}
      />
    )
  }

  if (!data) {
    return (
      <PortalStateCard
        title="No dashboard data"
        message="No data to show yet. Check back once your account has been set up."
        actionLabel="Refresh"
        onAction={loadDashboard}
        minHeight={260}
      />
    )
  }

  const { hero = {}, activity = {}, agenda = {}, manage = {}, office = {} } = data

  // ── Hero Metrics Row ──
  const renderHero = () => (
    <Box className={classes.heroRow}>
      {HERO_CARDS.map((cfg) => (
        <Card
          key={cfg.key}
          className={classes.heroCard}
          style={{
            background: 'var(--df-surface)',
            borderLeft: `3px solid ${cfg.border}`,
            border: '1px solid var(--df-line)',
            borderLeftWidth: 3,
            borderLeftColor: cfg.border
          }}
        >
          <CardContent className={classes.heroCardContent}>
            <Typography className={classes.heroValue}>
              {hero[cfg.key] != null ? hero[cfg.key] : 0}
            </Typography>
            <Typography className={classes.heroLabel}>
              {cfg.label}
            </Typography>
          </CardContent>
        </Card>
      ))}
      <Tooltip title="Refresh dashboard">
        <IconButton className={classes.refreshBtn} onClick={loadDashboard} size="small">
          <Refresh fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )

  // ── Activity Quadrant ──
  const renderActivity = () => {
    const items = activity.items || []

    return (
      <Card className={classes.quadrantCard}>
        <Box className={classes.quadrantHeader}>
          <Box display="flex" alignItems="center">
            <Typography className={classes.quadrantTitle}>Activity</Typography>
            <Typography className={classes.quadrantSubtitle}>Today's field events</Typography>
          </Box>
          <Box display="flex" gap={4} style={{ gap: 6 }}>
            {hero.completedToday > 0 && (
              <Chip
                size="small"
                label={`${hero.completedToday} done`}
                className={`${classes.headerChip} df-chip-ok`}
              />
            )}
            {hero.photosToday > 0 && (
              <Chip
                size="small"
                label={`${hero.photosToday} photos`}
                className={`${classes.headerChip} df-chip-info`}
              />
            )}
            {hero.openIssues > 0 && (
              <Chip
                size="small"
                label={`${hero.openIssues} issues`}
                className={`${classes.headerChip} df-chip-danger`}
              />
            )}
          </Box>
        </Box>

        <Box className={classes.quadrantBody}>
          {items.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)' }}>No field activity yet today</Typography>
              <Typography variant="caption" style={{ color: 'var(--df-ink-4)' }}>Events will appear here as your crews work.</Typography>
            </Box>
          ) : (
            items.map((item, idx) => {
              const isUrgent = item.type === 'issue' && item.priority === 'urgent'
              const iconCfg = feedIconProps(item.type)

              return (
                <Box
                  key={item._id || idx}
                  className={`${classes.feedItem} ${isUrgent ? classes.feedItemUrgent : ''}`}
                >
                  <Avatar className={classes.feedIcon} style={iconCfg.style}>
                    {iconCfg.children}
                  </Avatar>
                  <Box className={classes.feedBody}>
                    <Typography className={classes.feedPrimary}>
                      <strong>{item.employeeNameShort || 'Unknown'}</strong>
                      {feedActionText(item)}
                    </Typography>
                    {feedPropertyLabel(item) && (
                      <Typography className={classes.feedSecondary}>
                        {feedPropertyLabel(item)}
                        {item.address ? ` — ${item.address}` : ''}
                      </Typography>
                    )}
                    {item.completionNotes && (
                      <Typography className={classes.feedSecondary} style={{ fontStyle: 'italic' }}>
                        {item.completionNotes}
                      </Typography>
                    )}
                    {item.issueNotes && (
                      <Typography className={classes.feedSecondary} style={{ fontStyle: 'italic' }}>
                        {item.issueNotes}
                      </Typography>
                    )}
                    {item.noteBody && (
                      <Typography className={classes.feedSecondary} style={{ fontStyle: 'italic' }}>
                        {item.noteBody}
                      </Typography>
                    )}
                    {item.type === 'photo' && item.thumbnails?.length > 0 && (
                      <Box className={classes.thumbnailRow}>
                        {item.thumbnails.slice(0, 4).map((src, ti) => (
                          <img key={ti} src={src} alt="" className={classes.thumbnail} />
                        ))}
                      </Box>
                    )}
                    {isUrgent && (
                      <Chip
                        size="small"
                        label="URGENT"
                        className="df-chip-danger"
                        style={{ height: 18, marginTop: 4 }}
                      />
                    )}
                  </Box>
                  <Typography className={classes.feedTime}>
                    {formatShortTime(item.timestamp)}
                  </Typography>
                </Box>
              )
            })
          )}
        </Box>

        <Box className={classes.quadrantFooter}>
          <Button className={classes.footerLink} size="small" onClick={() => history.push(`${url}/schedule`)}>
            View All Activity &gt;
          </Button>
        </Box>
      </Card>
    )
  }

  // ── Agenda Quadrant ──
  const renderAgenda = () => {
    const overdueItems = agenda.overdue || []
    const dueSoonItems = agenda.dueSoon || []
    const hasContent = overdueItems.length > 0 || dueSoonItems.length > 0

    return (
      <Card className={classes.quadrantCard}>
        <Box className={classes.quadrantHeader}>
          <Box display="flex" alignItems="center">
            <Typography className={classes.quadrantTitle}>Agenda</Typography>
            <Typography className={classes.quadrantSubtitle}>What needs attention</Typography>
          </Box>
          <Box display="flex" style={{ gap: 6 }}>
            {(agenda.totalOverdue || 0) > 0 && (
              <Chip
                size="small"
                label={`${agenda.totalOverdue} overdue`}
                className={`${classes.headerChip} df-chip-danger`}
              />
            )}
            {(agenda.totalDueSoon || 0) > 0 && (
              <Chip
                size="small"
                label={`${agenda.totalDueSoon} due soon`}
                className={`${classes.headerChip} df-chip-warn`}
              />
            )}
          </Box>
        </Box>

        <Box className={classes.quadrantBody}>
          {!hasContent ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)' }}>Nothing overdue</Typography>
              <Typography variant="caption" style={{ color: 'var(--df-ink-4)' }}>You're all caught up — nice work.</Typography>
            </Box>
          ) : (
            <>
              {overdueItems.length > 0 && (
                <>
                  <Typography className={classes.agendaSectionLabel} style={{ color: 'var(--df-danger-text)' }}>
                    OVERDUE
                  </Typography>
                  {overdueItems.map((item, idx) => (
                    <Box key={item.scheduleItemId || idx} className={classes.agendaItem} style={{ cursor: 'pointer' }} onClick={() => setSelectedScheduleItemId(item.scheduleItemId)}>
                      <Box>
                        <Typography className={classes.agendaSiteName}>
                          {item.siteName || 'Unknown site'}
                        </Typography>
                        <Typography className={classes.agendaAddress}>
                          {item.address || 'No address'}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${item.daysLate || '?'}d late`}
                        className="df-chip-danger"
                      />
                    </Box>
                  ))}
                </>
              )}

              {dueSoonItems.length > 0 && (
                <>
                  <Typography className={classes.agendaSectionLabel} style={{ color: 'var(--df-warn-text)', marginTop: overdueItems.length > 0 ? 8 : 0 }}>
                    DUE THIS WEEK
                  </Typography>
                  {dueSoonItems.map((item, idx) => (
                    <Box key={item.scheduleItemId || idx} className={classes.agendaItem} style={{ cursor: 'pointer' }} onClick={() => setSelectedScheduleItemId(item.scheduleItemId)}>
                      <Box>
                        <Typography className={classes.agendaSiteName}>
                          {item.siteName || 'Unknown site'}
                        </Typography>
                        <Typography className={classes.agendaAddress}>
                          {item.address || 'No address'}
                        </Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.75rem', color: 'var(--df-warn-text)', fontWeight: 600 }}>
                        {formatPortalDate(item.nextScheduledDate)}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </>
          )}
        </Box>

        <Box className={classes.quadrantFooter}>
          <Button className={classes.footerLink} size="small" onClick={() => history.push(`${url}/schedule`)}>
            View Full Schedule &gt;
          </Button>
        </Box>
      </Card>
    )
  }

  // ── Manage Quadrant ──
  const renderManage = () => {
    const crews = manage.crews || []
    const coverage = manage.coverage || {}

    const emptyState = isAdmin
      ? { title: 'No crews set up yet', message: 'Head to the Crews tab to create your first crew.' }
      : { title: "You haven't been assigned to a crew yet", message: 'Your admin will assign you soon.' }

    return (
      <Card className={classes.quadrantCard}>
        <Box className={classes.quadrantHeader}>
          <Box display="flex" alignItems="center">
            <Typography className={classes.quadrantTitle}>Manage</Typography>
            <Typography className={classes.quadrantSubtitle}>Team & territory overview</Typography>
          </Box>
        </Box>

        <Box className={classes.quadrantBody}>
          {/* Coverage stats */}
          {(coverage.totalSites != null) && (
            <Box className={classes.coverageRow}>
              <Box className={classes.coverageStat}>
                <Typography className={classes.coverageValue}>{coverage.totalSites || 0}</Typography>
                <Typography className={classes.coverageLabel}>Total Sites</Typography>
              </Box>
              <Box className={classes.coverageStat}>
                <Typography className={classes.coverageValue}>{coverage.assigned || 0}</Typography>
                <Typography className={classes.coverageLabel}>Assigned</Typography>
              </Box>
              <Box className={classes.coverageStat}>
                <Typography className={classes.coverageValue}>
                  {coverage.unassigned || 0}
                </Typography>
                <Typography className={classes.coverageLabel}>Unassigned</Typography>
              </Box>
            </Box>
          )}

          {/* Crew rows */}
          {crews.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)' }}>{emptyState.title}</Typography>
              <Typography variant="caption" style={{ color: 'var(--df-ink-4)' }}>{emptyState.message}</Typography>
            </Box>
          ) : (
            crews.map((crew, idx) => (
              <Box key={crew.crewId || idx} className={classes.crewRow}>
                <Box>
                  <Typography className={classes.crewName}>
                    {crew.name || 'Unnamed Crew'}
                  </Typography>
                  <Typography className={classes.crewMeta}>
                    {crew.memberCount || 0} member{(crew.memberCount || 0) !== 1 ? 's' : ''} &middot; {crew.siteCount || 0} site{(crew.siteCount || 0) !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${crew.completedToday || 0} done`}
                  className="df-chip-ok"
                />
              </Box>
            ))
          )}

          {/* Quick links — admin only */}
          {isAdmin && (
            <Box className={classes.quickLinks}>
              <Button className={classes.pillBtn} size="small" onClick={() => history.push(`${url}/crews`)}>
                Crews &gt;
              </Button>
              <Button className={classes.pillBtn} size="small" onClick={() => history.push(`${url}/users`)}>
                Users &gt;
              </Button>
              <Button className={classes.pillBtn} size="small" onClick={() => history.push(`${url}/territories`)}>
                Territories &gt;
              </Button>
            </Box>
          )}
        </Box>

        <Box className={classes.quadrantFooter}>
          <Button className={classes.footerLink} size="small" onClick={() => history.push(`${url}/crews`)}>
            Manage Crews &gt;
          </Button>
        </Box>
      </Card>
    )
  }

  // ── Office Quadrant ──
  const renderOffice = () => {
    const items = office.items || []
    const unreadCount = office.unreadCount || 0

    return (
      <Card className={classes.quadrantCard}>
        <Box className={classes.quadrantHeader}>
          <Box display="flex" alignItems="center">
            <Typography className={classes.quadrantTitle}>Office</Typography>
            {unreadCount > 0 && (
              <Badge
                badgeContent={unreadCount}
                color="primary"
                style={{ marginLeft: 12 }}
              >
                <span />
              </Badge>
            )}
          </Box>
        </Box>

        <Box className={classes.quadrantBody}>
          {office.messagingDisabled === true ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)' }}>Messaging not enabled</Typography>
              <Typography variant="caption" style={{ color: 'var(--df-ink-4)' }}>Messaging is not enabled for your account.</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)' }}>No messages from the office yet</Typography>
              <Typography variant="caption" style={{ color: 'var(--df-ink-4)' }}>Conversations with your manager will appear here.</Typography>
            </Box>
          ) : (
            items.map((msg, idx) => (
              <Box
                key={msg.threadId || idx}
                className={classes.messageRow}
                style={{ cursor: 'pointer' }}
                onClick={() => history.push(`${url}/messages/${msg.threadId}`)}
              >
                <Avatar className={msg.unread ? classes.msgIconUnread : classes.msgIconRead}>
                  {'\u2709'}
                </Avatar>
                <Box className={classes.msgBody}>
                  <Typography className={`${classes.msgTitle} ${msg.unread ? classes.msgTitleUnread : ''}`}>
                    {msg.title || 'No subject'}
                  </Typography>
                  <Typography className={classes.msgPreview}>
                    {msg.preview || ''}
                  </Typography>
                  <Typography className={classes.msgMeta}>
                    {msg.authorDisplayName || 'Unknown'} &middot; {relativeTime(msg.lastMessageAt)}
                  </Typography>
                </Box>
                {msg.unread && <Box className={classes.unreadDot} />}
              </Box>
            ))
          )}
        </Box>

        <Box className={classes.quadrantFooter}>
          <Button className={classes.footerLink} size="small" onClick={() => history.push(`${url}/messages`)}>
            View All Messages &gt;
          </Button>
        </Box>
      </Card>
    )
  }

  // ── Main Render ──
  return (
    <Box>
      {renderHero()}

      <Grid container spacing={3}>
        {/* Top row */}
        <Grid item xs={12} md={6}>
          {renderActivity()}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderAgenda()}
        </Grid>

        {/* Bottom row */}
        <Grid item xs={12} md={6}>
          {renderManage()}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderOffice()}
        </Grid>
      </Grid>

      <SubcontractorSiteDetailDrawer
        scheduleItemId={selectedScheduleItemId}
        open={Boolean(selectedScheduleItemId)}
        onClose={() => setSelectedScheduleItemId(null)}
      />
    </Box>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorDashboardV2)
